// In-app error reports (operator only, /admin/errors). Server and browser
// failures grouped by cause, from /api/admin/errors (backend/error_reports.py).
// Resolve hides a report until the error happens again; Ignore keeps it quiet.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import { API } from '../App';
import { useT } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

const STATUS_TABS = ['open', 'resolved', 'ignored', 'all'];

function formatTimestamp(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (e) {
    return String(value);
  }
}

function sampleSummary(sample) {
  const parts = [
    sample.method || sample.api_method,
    sample.route || sample.path || sample.api_path || sample.page,
    sample.status_code || sample.api_status,
    sample.request_id ? 'req ' + sample.request_id : null,
    sample.release ? 'rev ' + String(sample.release).slice(0, 8) : null,
    sample.user_id ? 'user ' + sample.user_id : null,
    sample.task ? 'task ' + sample.task : null,
  ];
  return parts.filter(Boolean).join(' · ');
}

export default function AdminErrors() {
  const { t } = useT();
  const [status, setStatus] = useState('open');
  const [source, setSource] = useState('all');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [data, setData] = useState({ items: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const statusLabel = (value) => {
    const labels = {
      open: t('Unresolved'),
      resolved: t('Resolved'),
      ignored: t('Ignored'),
      all: t('All'),
    };
    return labels[value] || value;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { status, source };
      if (query) params.q = query;
      const r = await axios.get(API + '/admin/errors', { params });
      const body = r.data || {};
      setData({
        items: Array.isArray(body.items) ? body.items : [],
        counts: body.counts && typeof body.counts === 'object' ? body.counts : {},
        total: Number(body.total) || 0,
      });
    } catch (e) {
      const detail = e && e.response && e.response.data ? e.response.data.detail : null;
      setError(typeof detail === 'string' ? detail : t('Could not load error reports'));
    } finally {
      setLoading(false);
    }
  }, [status, source, query, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openReport = async (fingerprint) => {
    try {
      const r = await axios.get(API + '/admin/errors/' + fingerprint);
      setSelected(r.data || null);
    } catch (e) {
      toast.error(t('Could not load the report'));
    }
  };

  const changeStatus = async (next) => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await axios.patch(API + '/admin/errors/' + selected.fingerprint, { status: next });
      setSelected(r.data || null);
      toast.success(t('Report updated'));
      load();
    } catch (e) {
      toast.error(t('Could not update the report'));
    } finally {
      setBusy(false);
    }
  };

  const onSearch = (event) => {
    event.preventDefault();
    setQuery(search.trim());
  };

  const samples = selected && Array.isArray(selected.samples) ? selected.samples.slice().reverse() : [];

  return (
    <div className="xp-page">
      <Card data-testid="admin-errors-card">
        <CardHeader className="flex flex-row items-start justify-between gap-2">
          <div>
            <CardTitle>{t('Error reports')}</CardTitle>
            <CardDescription>
              {t(
                'Server and browser errors grouped by cause. Resolve hides a report until the error happens again; Ignore keeps it quiet.'
              )}
            </CardDescription>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-errors-refresh">
            <RefreshCw className={'w-4 h-4 mr-1 ' + (loading ? 'animate-spin' : '')} />
            {t('Refresh')}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Tabs value={status} onValueChange={setStatus}>
              <TabsList>
                {STATUS_TABS.map((value) => (
                  <TabsTrigger key={value} value={value} data-testid={'admin-errors-tab-' + value}>
                    {statusLabel(value)}
                    {value !== 'all' && data.counts[value] !== undefined ? ' (' + data.counts[value] + ')' : ''}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="w-40" data-testid="admin-errors-source">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('All sources')}</SelectItem>
                <SelectItem value="server">{t('Server')}</SelectItem>
                <SelectItem value="client">{t('Browser')}</SelectItem>
              </SelectContent>
            </Select>
            <form onSubmit={onSearch} className="flex gap-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('Search by error text')}
                className="w-64"
                data-testid="admin-errors-search"
              />
              <Button type="submit" variant="outline">
                {t('Search')}
              </Button>
            </form>
          </div>
          {error ? <p className="text-sm text-red-400 mb-3">{error}</p> : null}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('Error')}</TableHead>
                <TableHead>{t('Source')}</TableHead>
                <TableHead className="text-right">{t('Count')}</TableHead>
                <TableHead>{t('Last seen')}</TableHead>
                <TableHead>{t('Status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    {loading ? t('Loading…') : t('No error reports')}
                  </TableCell>
                </TableRow>
              ) : (
                data.items.map((item) => (
                  <TableRow
                    key={item.fingerprint}
                    className="cursor-pointer"
                    onClick={() => openReport(item.fingerprint)}
                    data-testid="admin-errors-row"
                  >
                    <TableCell className="max-w-xl">
                      <div className="font-medium truncate">{item.last_title || item.title}</div>
                      <div className="text-xs text-muted-foreground truncate">{item.last_where}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{item.source === 'client' ? t('Browser') : t('Server')}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{item.count}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatTimestamp(item.last_seen)}</TableCell>
                    <TableCell>
                      <Badge variant={item.status === 'open' ? 'destructive' : 'secondary'}>
                        {statusLabel(item.status)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(selected)}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {selected ? (
            <>
              <DialogHeader>
                <DialogTitle className="break-words">{selected.title}</DialogTitle>
                <DialogDescription>
                  {t('Seen {n} times', { n: selected.count || 0 })}
                  {' · '}
                  {t('First seen')}: {formatTimestamp(selected.first_seen)}
                  {' · '}
                  {t('Last seen')}: {formatTimestamp(selected.last_seen)}
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-wrap gap-2 my-3">
                {selected.status !== 'resolved' ? (
                  <Button onClick={() => changeStatus('resolved')} disabled={busy}>
                    {t('Mark resolved')}
                  </Button>
                ) : null}
                {selected.status !== 'ignored' ? (
                  <Button variant="outline" onClick={() => changeStatus('ignored')} disabled={busy}>
                    {t('Ignore')}
                  </Button>
                ) : null}
                {selected.status !== 'open' ? (
                  <Button variant="outline" onClick={() => changeStatus('open')} disabled={busy}>
                    {t('Reopen')}
                  </Button>
                ) : null}
              </div>
              {samples.map((sample, index) => (
                <div key={index} className="border border-border rounded-md p-3 mb-3 text-xs">
                  <div className="text-muted-foreground mb-2">
                    {formatTimestamp(sample.at)} {sampleSummary(sample)}
                  </div>
                  {sample.message ? <div className="mb-2 break-words">{sample.message}</div> : null}
                  {sample.stack ? (
                    <pre className="whitespace-pre-wrap break-words bg-surface-2 p-2 rounded max-h-64 overflow-auto">
                      {sample.stack}
                    </pre>
                  ) : null}
                  {sample.component_stack ? (
                    <pre className="whitespace-pre-wrap break-words bg-surface-2 p-2 rounded max-h-40 overflow-auto mt-2">
                      {sample.component_stack}
                    </pre>
                  ) : null}
                </div>
              ))}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
